import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import ProductGrid from './ProductGrid';
import logo from '../assets/pokeshani-logo.png';
import './StoreView.css';
import SEO from './SEO';

interface CreatorProfile {
  fullName: string;
  email: string;
  twitter_url?: string;
  instagram_url?: string;
}

const StoreView: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCreatorProfile = async () => {
      try {
        if (!username) {
          setError('Invalid store URL');
          return;
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, email, twitter_url, instagram_url')
          .ilike('email', `${username}@%`)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setError('Store not found');
          return;
        }

        setProfile({
          fullName: data.full_name,
          email: data.email,
          twitter_url: data.twitter_url,
          instagram_url: data.instagram_url
        });
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load store');
      } finally {
        setLoading(false);
      }
    };

    fetchCreatorProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="error-container">
        <h1 className="error-title">😕 {error || 'Store not found'}</h1>
        <p className="error-message">Please check the URL and try again</p>
      </div>
    );
  }

  return (
    <div className="store-container">
      <div className="store-content">
        {profile && username && (
          <SEO
            title={`${profile.fullName}'s Store - PokeShani`}
            description={`Explore digital products by ${profile.fullName} on PokeShani. Buy courses, ebooks, templates, and more with instant PayPal payouts.`}
            keywords="creator store, digital products, courses, ebooks, templates, instant payouts"
            url={`https://pokeshani.com/store/${username}`}
            type="profile"
          />
        )}
        <div className="store-header">
          {/* Logo and Site Name */}
          <div className="logo-container">
            <Link to="/">
              <img src={logo} alt="PokeShani Logo" className="header-logo" />
            </Link>
            <div className="site-name-container">
              <span className="site-name">PokeShani</span>
              <span className="store-label">{profile.fullName}'s Store</span>
            </div>
          </div>
          
          
          
          {(profile.twitter_url || profile.instagram_url) && (
            <div className="social-links">
              {profile.twitter_url && (
                <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="social-link">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.643 4.937c-.835.37-1.732.62-2.675.733.962-.576 1.7-1.49 2.048-2.578-.9.534-1.897.922-2.958 1.13-.85-.904-2.06-1.47-3.4-1.47-2.572 0-4.658 2.086-4.658 4.66 0 .364.042.718.12 1.06-3.873-.195-7.304-2.05-9.602-4.868-.4.69-.63 1.49-.63 2.342 0 1.616.823 3.043 2.072 3.878-.764-.025-1.482-.234-2.11-.583v.06c0 2.257 1.605 4.14 3.737 4.568-.392.106-.803.162-1.227.162-.3 0-.593-.028-.877-.082.593 1.85 2.313 3.198 4.352 3.234-1.595 1.25-3.604 1.995-5.786 1.995-.376 0-.747-.022-1.112-.065 2.062 1.323 4.51 2.093 7.14 2.093 8.57 0 13.255-7.098 13.255-13.254 0-.2-.005-.402-.014-.602.91-.658 1.7-1.477 2.323-2.41z"/>
                  </svg>
                </a>
              )}
              {profile.instagram_url && (
                <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="social-link instagram">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </a>
              )}
            </div>
          )}
        </div>

        <div className="products-section">
          <ProductGrid creatorEmail={profile.email} mode="store" />
        </div>
        
        {/* Platform Disclaimer at Bottom */}
        <div className="platform-disclaimer">
          <p><strong>ℹ️ Platform Notice:</strong> PokeShani is only a platform that connects buyers and sellers of digital products. We do not sell products, handle payments, or act as a financial intermediary. All transactions occur directly between buyers and sellers. <Link to="/terms-of-service">See full Terms of Service</Link>.</p>
        </div>
        
        {/* Footer */}
        <footer className="store-footer">
          <div className="footer-content">
            <div className="footer-bottom">
              <p>© {new Date().getFullYear()} PokeShani All rights reserved.</p>
              <div className="footer-links">
                <Link to="/privacy-policy" className="footer-link">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="footer-link">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StoreView;
